import { useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Grid, Stack, Typography } from '@mui/material'
import MDBox from 'components/MDBox'
import Page from '../pages/Page'
import SchemaTable from '../table/SchemaTable'
import FormattedDate from '../table/FormattedDate'
import useQueryLibrary from '../../hooks/useQueryLibrary'
import FetchContext from '../../context/fetch/FetchContext'
import {
    DASH,
    resolveActionStats,
    resolveAnnouncementStats,
} from '../../data-organizers/announcements/resolveAnnouncementStats'

const FIGURES = [
    { name: 'viewings', label: 'צפיות' },
    { name: 'barViewings', label: 'צפיות בסרגל' },
    { name: 'popupViewings', label: 'צפיות בחלון' },
    { name: 'viewingDuration', label: 'זמן צפייה' },
    { name: 'barViewingDuration', label: 'זמן צפייה בסרגל' },
    { name: 'popupViewingDuration', label: 'זמן צפייה בחלון' },
    { name: 'viewers', label: 'צופים' },
    { name: 'conversions', label: 'פניות' },
    { name: 'converters', label: 'פונים' },
    { name: 'averageViewingsPerUser', label: 'צפיות ממוצעות לצופה' },
    { name: 'averageViewingDurationPerUser', label: 'זמן צפייה ממוצע לצופה' },
]

// A dash is what "nothing happened" looks like, so it is passed through as text rather
// than handed to a date formatter that would turn it into an invalid date.
const dateCell = (value) => (value === DASH ? DASH : <FormattedDate value={value} />)

const COLUMNS = [
    { Header: 'שם פרטי', accessor: 'firstName' },
    { Header: 'שם משפחה', accessor: 'lastName' },
    { Header: 'שם משתמש', accessor: 'username' },
    { Header: 'דוא״ל', accessor: 'email' },
    { Header: 'צפיות בסרגל', accessor: 'barViewings', align: 'center' },
    { Header: 'צפיות בחלון', accessor: 'popupViewings', align: 'center' },
    { Header: 'זמן בסרגל', accessor: 'barViewingDuration', align: 'center' },
    { Header: 'זמן בחלון', accessor: 'popupViewingDuration', align: 'center' },
    { Header: 'פניות', accessor: 'conversions', align: 'center' },
    {
        Header: 'צפייה אחרונה',
        accessor: 'lastImpressionAt',
        Cell: (cell) => dateCell(cell.row.original.lastImpressionAt),
    },
    {
        Header: 'פנייה אחרונה',
        accessor: 'lastConversionAt',
        Cell: (cell) => dateCell(cell.row.original.lastConversionAt),
    },
]

export const ACTION_COLUMNS = [
    { Header: 'פעולה', accessor: 'name' },
    { Header: 'פניות', accessor: 'conversions', align: 'center' },
    { Header: 'פונים', accessor: 'converters', align: 'center' },
    { Header: 'הושלמו', accessor: 'success', align: 'center' },
    { Header: 'ממתינות', accessor: 'pending', align: 'center' },
    { Header: 'ננטשו', accessor: 'abandoned', align: 'center' },
    { Header: 'נכשלו', accessor: 'error', align: 'center' },
]

export const ACTIONS_TITLE = 'פניות לפי פעולה'

function Figure({ name, label, value }) {
    return (
        <MDBox
            data-testid={`figure-${name}`}
            sx={{
                padding: 2,
                border: 1,
                borderColor: 'grey.300',
                borderRadius: 1,
                height: '100%',
            }}
        >
            <Stack spacing={0.5}>
                <Typography variant="caption" color="text">{label}</Typography>
                <Typography variant="h4" component="p">{value}</Typography>
            </Stack>
        </MDBox>
    )
}

export default function AnnouncementStatsPage() {
    const { documentId } = useParams()
    const queryLibrary = useQueryLibrary()
    const fetcher = useContext(FetchContext)

    const { data: stats, isLoading } = useQuery({
        queryKey: queryLibrary.announcementStatsQuery(documentId),
        queryFn: () => fetcher.fetchAnnouncementStats(documentId),
    })

    const { figures, rows } = resolveAnnouncementStats(stats)
    const actionRows = resolveActionStats(stats)

    return (
        <Page className="announcement-stats" title="נתוני מודעה" busy={isLoading}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {FIGURES.map(({ name, label }) => (
                    <Grid item key={name} xs={6} md={4} lg={3}>
                        <Figure name={name} label={label} value={figures[name]} />
                    </Grid>
                ))}
            </Grid>

            {actionRows.length > 0 && (
                <MDBox sx={{ mb: 3 }}>
                    <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                        {ACTIONS_TITLE}
                    </Typography>
                    <SchemaTable data={actionRows} columns={ACTION_COLUMNS} />
                </MDBox>
            )}

            <SchemaTable data={rows} columns={COLUMNS} />
        </Page>
    )
}
